<template>
  <!-- HTML согласия показывается абитуриенту перед отправкой публичной анкеты. -->
  <v-card class="personal-data-settings" elevation="0">
    <v-card-title class="personal-data-settings__header">
      <div>
        <h2 class="personal-data-settings__title">Защита персональных данных</h2>
        <p class="personal-data-settings__description">
          Текст показывается абитуриенту перед заполнением заявления.
        </p>
      </div>
    </v-card-title>

    <v-card-text class="personal-data-settings__content">
      <v-alert
        v-if="error"
        class="personal-data-settings__alert"
        type="error"
        variant="tonal"
        closable
        @click:close="error = ''"
      >
        {{ error }}
      </v-alert>

      <v-alert
        v-if="saved"
        class="personal-data-settings__alert"
        type="success"
        variant="tonal"
        closable
        @click:close="saved = false"
      >
        Текст согласия сохранён.
      </v-alert>

      <div v-if="loading">
        <v-skeleton-loader type="heading, paragraph, article, actions" />
      </div>

      <template v-else>
        <div class="personal-data-settings__label">
          Текст согласия на обработку персональных данных
        </div>

        <div class="personal-data-settings__editor">
          <ckeditor
            v-model="text"
            :editor="editor"
            :config="editorConfig"
            :disabled="saving"
            @input="markAsChanged"
          />
        </div>

        <div class="personal-data-settings__actions">
          <v-btn
            color="primary"
            variant="flat"
            prepend-icon="mdi-content-save-outline"
            :loading="saving"
            :disabled="!changed"
            @click="saveConsent"
          >
            Сохранить
          </v-btn>

          <v-btn
            variant="text"
            prepend-icon="mdi-restore"
            :disabled="saving || text === defaultText"
            @click="restoreDefault"
          >
            Вернуть текст по умолчанию
          </v-btn>
        </div>
      </template>
    </v-card-text>
  </v-card>
</template>

<script>
import {
  ClassicEditor,
  AccessibilityHelp,
  Autoformat,
  AutoLink,
  Bold,
  Essentials,
  FontBackgroundColor,
  FontColor,
  GeneralHtmlSupport,
  Heading,
  Italic,
  Link,
  List,
  Paragraph,
  PasteFromOffice,
  SelectAll,
  SourceEditing,
  Table,
  TableToolbar,
  Underline,
  Undo
} from 'ckeditor5';
import { Ckeditor } from '@ckeditor/ckeditor5-vue';
import 'ckeditor5/ckeditor5.css';

export default {
  name: 'PersonalDataSection',
  components: {
    Ckeditor
  },
  data() {
    return {
      editor: ClassicEditor,
      editorConfig: {
        plugins: [
          AccessibilityHelp,
          Autoformat,
          AutoLink,
          Bold,
          Essentials,
          FontBackgroundColor,
          FontColor,
          GeneralHtmlSupport,
          Heading,
          Italic,
          Link,
          List,
          Paragraph,
          PasteFromOffice,
          SelectAll,
          SourceEditing,
          Table,
          TableToolbar,
          Underline,
          Undo
        ],
        toolbar: {
          items: [
            'undo',
            'redo',
            '|',
            'sourceEditing',
            '|',
            'heading',
            '|',
            'fontColor',
            'fontBackgroundColor',
            '|',
            'bold',
            'italic',
            'underline',
            'link',
            '|',
            'bulletedList',
            'numberedList',
            'insertTable',
            '|',
            'selectAll',
            'accessibilityHelp'
          ],
          shouldNotGroupWhenFull: false
        },
        table: {
          contentToolbar: ['tableColumn', 'tableRow', 'mergeTableCells']
        },
        htmlSupport: {
          allow: [
            {
              name: /^(p|div|span|h[1-6]|ul|ol|li|blockquote|table|thead|tbody|tfoot|tr|th|td|a|strong|em|u|s|br)$/,
              styles: true,
              classes: true,
              attributes: true
            }
          ]
        }
      },
      text: '',
      defaultText: '',
      loading: true,
      saving: false,
      changed: false,
      saved: false,
      error: ''
    };
  },
  created() {
    this.loadConsent();
  },
  methods: {
    loadConsent() {
      this.loading = true;
      this.error = '';

      this.axios
        .get(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/getPersonalDataConsent'
        )
        .then((response) => {
          this.text = response.data.text || '';
          this.defaultText = response.data.defaultText || '';
          this.changed = false;
        })
        .catch((error) => {
          this.error = 'Не удалось загрузить текст согласия.';
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    markAsChanged() {
      if (!this.loading) {
        this.changed = true;
        this.saved = false;
      }
    },
    restoreDefault() {
      // Восстановление только подставляет утверждённый сервером текст;
      // окончательно он применяется после явного сохранения.
      this.text = this.defaultText;
      this.changed = true;
      this.saved = false;
    },
    saveConsent() {
      this.saving = true;
      this.error = '';
      this.saved = false;

      this.axios
        .post(
          this.$model.wwwroot +
            '/procollege/manage_groups/admission/config/setPersonalDataConsent',
          new URLSearchParams({ text: this.text })
        )
        .then((response) => {
          this.text = response.data.text || this.defaultText;
          this.changed = false;
          this.saved = true;
        })
        .catch((error) => {
          this.error = 'Не удалось сохранить текст согласия.';
          this.axiosDefaultErrorHandler(error);
        })
        .finally(() => {
          this.saving = false;
        });
    }
  }
};
</script>

<style scoped>
.personal-data-settings {
  border: 1px solid #dfe5ec;
  border-radius: 12px;
}

.personal-data-settings__header {
  padding: 24px 28px 18px;
  border-bottom: 1px solid #edf0f4;
  white-space: normal;
}

.personal-data-settings__title {
  margin: 0;
  color: #1d2939;
  font-size: 22px;
  font-weight: 600;
  line-height: 1.3;
}

.personal-data-settings__description {
  margin: 6px 0 0;
  color: #667085;
  font-size: 14px;
  font-weight: 400;
}

.personal-data-settings__content {
  padding: 24px 28px 28px;
}

.personal-data-settings__alert {
  margin-bottom: 20px;
}

.personal-data-settings__label {
  margin-bottom: 8px;
  color: #344054;
  font-size: 14px;
  font-weight: 600;
}

.personal-data-settings__editor {
  overflow: hidden;
  border: 1px solid #d0d5dd;
  border-radius: 8px;
}

.personal-data-settings__editor :deep(.ck.ck-editor__main > .ck-editor__editable) {
  min-height: 420px;
  max-height: 65vh;
  padding: 18px 24px;
  overflow-y: auto;
  border-right: 0;
  border-bottom: 0;
  border-left: 0;
  box-shadow: none;
}

.personal-data-settings__editor :deep(.ck.ck-toolbar) {
  border: 0;
  border-bottom: 1px solid #d0d5dd;
}

.personal-data-settings__actions {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 20px;
}

@media (max-width: 600px) {
  .personal-data-settings__header,
  .personal-data-settings__content {
    padding-right: 18px;
    padding-left: 18px;
  }

  .personal-data-settings__actions .v-btn {
    width: 100%;
  }
}
</style>
